import React from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  ArrowUpRight,
  ArrowRight,
  Plus,
  Check,
  MapPin,
  Clock,
  Users,
} from "lucide-react";

import { Reveal, TiltCard } from "../sections/_shared";
import InlineContactForm from "../sections/InlineContactForm";
import LeadForm from "../sections/LeadForm";
import { useProjects } from "../lib/projectsStore";
import { useTracker } from "../lib/useTracker";
import { stats, person } from "../data/profile";
import { cn } from "../lib/utils";
import { pages as landingPageList, getPage, labelOf, trailOf } from "../data/landingPages";
import pricing from "../data/pricing.json";

/** Look a page up by slug. */
export const getLandingPage = getPage;

export const LANDING_SLUGS = landingPageList.map((p) => p.slug);

export const breadcrumbTrail = trailOf;
export const titleOf = labelOf;

function Breadcrumbs({ trail }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-8">
      <ol className="flex flex-wrap items-center gap-1.5 text-[0.72rem] text-slate-500">
        {trail.map((c, i) => {
          const last = i === trail.length - 1;
          return (
            <li key={c.url} className="flex items-center gap-1.5">
              {last ? (
                <span aria-current="page" className="text-slate-400">
                  {c.name}
                </span>
              ) : (
                <>
                  <Link
                    to={c.url}
                    className="transition-colors hover:text-brand-300"
                  >
                    {c.name}
                  </Link>
                  <ChevronRight className="h-3 w-3 text-slate-700" />
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Numbered process steps. */
function Steps({ steps }) {
  return (
    <ol className="mt-6 space-y-3">
      {steps.map((s, i) => (
        <li key={s.t} className="flex gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 sm:p-5">
          <span className="mt-0.5 font-mono text-[0.62rem] text-brand-300/70">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span>
            <span className="block text-[0.92rem] font-semibold text-slate-100">
              {s.t}
            </span>
            <span className="mt-1.5 block text-[0.84rem] leading-relaxed text-slate-400">
              {s.d}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Definition-style capability list. */
function Bullets({ bullets }) {
  return (
    <ul className="mt-6 grid gap-3 sm:grid-cols-2">
      {bullets.map((b) => (
        <li key={b.t} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400 shadow-glow" />
            <div>
              <h3 className="text-[0.92rem] font-semibold leading-snug text-slate-100">
                {b.t}
              </h3>
              <p className="mt-2 text-[0.84rem] leading-relaxed text-slate-400">
                {b.d}
              </p>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Real projects used as evidence for the claims above them. */
function ProofProjects({ ids }) {
  const { projects } = useProjects();
  const picked = ids
    .map((id) => projects.find((p) => p.id === id))
    .filter(Boolean);

  if (!picked.length) return null;

  return (
    <section aria-labelledby="proof-heading" className="mt-14 sm:mt-20">
      <Reveal>
        <div className="flex items-center gap-4">
          <h2 id="proof-heading" className="text-fluid-lg font-semibold text-slate-100">
            Work this comes from
          </h2>
          <span className="rule hidden flex-1 sm:block" />
        </div>
      </Reveal>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {picked.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.06} className="h-full">
            <TiltCard intensity={5} className="h-full">
              <Link
                to={`/projects/${p.id}`}
                className="panel panel-hover group flex h-full flex-col overflow-hidden rounded-3xl"
              >
                {p.image && (
                  <img
                    src={p.image}
                    alt={`${p.title} — ${p.tagline}`}
                    loading="lazy"
                    decoding="async"
                    className="h-32 w-full object-cover object-top opacity-80 transition-opacity duration-500 group-hover:opacity-100"
                  />
                )}
                <div className="flex flex-1 flex-col p-5">
                  <p className="font-mono text-[0.55rem] uppercase tracking-label text-slate-600">
                    {p.category}
                  </p>
                  <h3 className="mt-2 font-display text-[1rem] font-semibold text-slate-50">
                    {p.title}
                  </h3>
                  <p className="mt-2 flex-1 text-[0.8rem] leading-relaxed text-slate-400">
                    {p.summary}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[0.75rem] text-brand-300">
                    Read the case study
                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </TiltCard>
          </Reveal>
        ))}
      </div>
    </section>
  );
}


/**
 * Pricing, shown only on pages that name a priced product.
 *
 * The figures come from src/data/pricing.json rather than the page copy,
 * because the same price appears on several pages, in the Offer node of
 * the JSON-LD and in llms.txt. A price stated in four places and edited
 * in three is worse than no price at all.
 */
function Pricing() {
  return (
    <section aria-labelledby="pricing-heading" className="mt-14 sm:mt-20">
      <Reveal>
        <div className="flex items-center gap-4">
          <h2 id="pricing-heading" className="text-fluid-lg font-semibold text-slate-100">
            What it costs
          </h2>
          <span className="rule hidden flex-1 sm:block" />
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <p className="aeo-answer mt-4 max-w-prose2 text-fluid-sm leading-relaxed text-slate-400">
          {pricing.summary}
        </p>
      </Reveal>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {pricing.tiers.map((tier, i) => (
          <Reveal key={tier.id} delay={0.08 + i * 0.05} className="h-full">
            <TiltCard intensity={5} className="h-full">
              <article
                className={cn(
                  "panel panel-hover flex h-full flex-col rounded-3xl p-6 sm:p-7",
                  tier.id === "per-seat" && "border-brand-400/25"
                )}
              >
                <p className="font-mono text-[0.6rem] uppercase tracking-label text-slate-500">
                  {tier.name}
                </p>
                <p className="mt-3 flex items-baseline gap-2">
                  <span className="gradient-text font-display text-fluid-2xl font-semibold leading-none">
                    {tier.price}
                  </span>
                  <span className="text-[0.78rem] text-slate-500">{tier.unit}</span>
                </p>
                <p className="mt-3 text-[0.86rem] leading-relaxed text-slate-400">
                  {tier.blurb}
                </p>

                <ul className="mt-5 flex-1 space-y-2.5 border-t border-white/[0.06] pt-5">
                  {tier.points.map((pt) => (
                    <li key={pt} className="flex gap-3 text-[0.82rem] leading-relaxed">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-400" />
                      <span className="text-slate-400">{pt}</span>
                    </li>
                  ))}
                </ul>

                <a href="#enquire" className="btn-ghost mt-6 w-full">
                  {tier.id === "per-seat" ? "Get started" : "Get a quote"}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </article>
            </TiltCard>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.18}>
        <p className="mt-4 text-[0.74rem] text-slate-600">
          {pricing.perSeat.note} {pricing.custom.note}
        </p>
      </Reveal>
    </section>
  );
}

/** FAQ — native <details> so answers stay in the DOM for crawlers. */
function Faqs({ faqs }) {
  if (!faqs?.length) return null;
  return (
    <section aria-labelledby="faq-heading" className="mt-14 sm:mt-20">
      <Reveal>
        <div className="flex items-center gap-4">
          <h2 id="faq-heading" className="text-fluid-lg font-semibold text-slate-100">
            Common questions
          </h2>
          <span className="rule hidden flex-1 sm:block" />
        </div>
      </Reveal>

      <ul className="mt-6 divide-y divide-white/[0.06] border-y border-white/[0.06]">
        {faqs.map((f, i) => (
          <li key={f.q}>
            <Reveal delay={Math.min(i * 0.04, 0.16)}>
              <details className="group" open={i === 0}>
                <summary className="flex cursor-pointer list-none items-start gap-4 py-5 [&::-webkit-details-marker]:hidden">
                  <h3 className="flex-1 font-display text-[0.98rem] font-medium leading-snug text-slate-100 transition-colors group-hover:text-white">
                    {f.q}
                  </h3>
                  <Plus
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform duration-400 ease-out2 group-open:rotate-45 group-open:text-brand-300"
                  />
                </summary>
                <p className="aeo-answer max-w-prose2 pb-6 pr-8 text-[0.86rem] leading-relaxed text-slate-400">
                  {f.a}
                </p>
              </details>
            </Reveal>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Cross-links to sibling pages, with descriptive anchors. */
function Related({ slugs }) {
  const pages = slugs.map(getPage).filter(Boolean);
  if (!pages.length) return null;

  return (
    <nav aria-labelledby="related-heading" className="mt-14 sm:mt-20">
      <h2 id="related-heading" className="text-fluid-lg font-semibold text-slate-100">
        Related
      </h2>
      <ul className="mt-5 grid gap-2 sm:grid-cols-2">
        {pages.map((p) => (
          <li key={p.slug}>
            <Link
              to={`/${p.slug}`}
              className="group flex items-start justify-between gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 transition-colors hover:border-brand-400/30"
            >
              <span>
                <span className="block text-[0.88rem] font-medium text-slate-200">
                  {titleOf(p)}
                </span>
                <span className="mt-1 block text-[0.76rem] leading-relaxed text-slate-500">
                  {p.h1}
                </span>
              </span>
              <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-600 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-300" />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default function LandingPage({ slug }) {
  const page = getLandingPage(slug);
  useTracker(page ? `Landing: ${slug}` : "Landing: unknown");

  if (!page) return null;

  const trail = breadcrumbTrail(page);

  // Short, scannable proof points. A recruiter scans before they read.
  const proofPoints = stats.slice(0, 4);

  return (
    <div className="pb-24">
      {/* ---------------- Hero ---------------- */}
      <div className="relative overflow-hidden border-b border-white/[0.06]">
        {/* Ambient wash — the landing pages don't mount the 3D canvas, so
            this keeps them visually part of the same site. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 15% 0%, rgba(16,185,129,0.10), transparent 60%)," +
              "radial-gradient(ellipse 60% 50% at 95% 20%, rgba(34,211,238,0.08), transparent 60%)",
          }}
        />

        <div className="shell pb-14 pt-8 sm:pt-12">
          <Breadcrumbs trail={trail} />

          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            {/* Pitch */}
            <header className="lg:col-span-7">
              <Reveal>
                <p className="eyebrow">{page.eyebrow}</p>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-4 text-fluid-2xl font-semibold leading-[1.06] tracking-tight">
                  {page.h1}
                </h1>
              </Reveal>
              {/* The AEO answer paragraph: names the entity, states the
                  fact, short enough to be lifted whole. */}
              <Reveal delay={0.1}>
                <p className="aeo-summary mt-6 max-w-prose2 text-fluid-base leading-relaxed text-slate-300">
                  {page.lead}
                </p>
              </Reveal>

              <Reveal delay={0.16}>
                <ul className="mt-7 flex flex-wrap gap-x-5 gap-y-2.5">
                  {[
                    [MapPin, `${person.location.city}, ${person.location.region} · Remote worldwide`],
                    [Clock, "Replies within a working day"],
                    [Users, "Leads a 10-engineer team"],
                  ].map(([Icon, label]) => (
                    <li
                      key={label}
                      className="inline-flex items-center gap-2 text-[0.78rem] text-slate-400"
                    >
                      <Icon className="h-3.5 w-3.5 text-brand-400/70" />
                      {label}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a href="#enquire" className="btn-primary lg:hidden">
                    Get in touch
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <Link to="/projects" className="btn-ghost">
                    See the work
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </Reveal>
            </header>

            {/* Enquiry card — above the fold on desktop, immediately after
                the pitch on mobile. */}
            <div className="lg:col-span-5">
              <Reveal delay={0.12}>
                <div id="enquire-top" className="lg:sticky lg:top-24">
                  <LeadForm
                    source={`Landing hero: /${page.slug}`}
                    role={
                      page.kind === "hire"
                        ? titleOf(page).replace(/^Hire /i, "")
                        : null
                    }
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------- Trust bar ---------------- */}
      <div className="border-b border-white/[0.06] bg-white/[0.015]">
        <div className="shell">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-6 py-8 sm:grid-cols-4">
            {proofPoints.map((st) => (
              <div key={st.label}>
                <dt className="sr-only">{st.label}</dt>
                <dd>
                  <span className="gradient-text block font-display text-fluid-xl font-semibold leading-none">
                    {st.value}
                    <span className="ml-1 font-mono text-[0.6rem] uppercase tracking-wider2 text-slate-500">
                      {st.unit}
                    </span>
                  </span>
                  <span className="mt-2 block text-[0.76rem] leading-snug text-slate-400">
                    {st.label}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Product shot, where the page describes something with a real
          interface. Eager + fetchpriority because on these pages it is
          the largest contentful element. */}
      {page.heroImage && (
        <div className="shell">
          <Reveal>
            <figure className="mt-12 sm:mt-16">
              <img
                src={page.heroImage.src}
                alt={page.heroImage.alt}
                loading="eager"
                fetchpriority="high"
                decoding="async"
                className="w-full rounded-3xl border border-white/[0.07] object-cover object-top"
              />
              {page.heroImage.caption && (
                <figcaption className="mt-3 text-center text-[0.74rem] text-slate-600">
                  {page.heroImage.caption}
                </figcaption>
              )}
            </figure>
          </Reveal>
        </div>
      )}

      <div className="shell">
        {page.intro?.map((para, i) => (
          <Reveal key={i} delay={i * 0.04}>
            <p
              className={cn(
                "max-w-prose2 text-fluid-sm leading-relaxed text-slate-400",
                i === 0 ? "mt-14 sm:mt-16" : "mt-4"
              )}
            >
              {para}
            </p>
          </Reveal>
        ))}
      {page.sections.map((sec, i) => (
        <section
          key={sec.h2}
          aria-labelledby={`sec-${i}`}
          className={cn("mt-14 sm:mt-20")}
        >
          <Reveal>
            <div className="flex items-center gap-4">
              <h2
                id={`sec-${i}`}
                className="text-fluid-lg font-semibold text-slate-100"
              >
                {sec.h2}
              </h2>
              <span className="rule hidden flex-1 sm:block" />
            </div>
          </Reveal>

          {sec.body?.map((para, j) => (
            <Reveal key={j} delay={0.05 + j * 0.04}>
              <p className="mt-4 max-w-prose2 text-fluid-sm leading-relaxed text-slate-400">
                {para}
              </p>
            </Reveal>
          ))}

          {sec.bullets && (
            <Reveal delay={0.08}>
              <Bullets bullets={sec.bullets} />
            </Reveal>
          )}
          {sec.steps && (
            <Reveal delay={0.08}>
              <Steps steps={sec.steps} />
            </Reveal>
          )}
        </section>
      ))}

      {page.proofProjects && <ProofProjects ids={page.proofProjects} />}

      {page.showPricing && <Pricing />}

      <Faqs faqs={page.faqs} />

      <div className="mt-14 sm:mt-20">
        <InlineContactForm
          source={`Landing: /${page.slug}`}
          heading={page.cta?.h2}
          body={page.cta?.body}
        />
      </div>

      <Related slugs={page.related} />
      </div>
    </div>
  );
}
